import { BaseApiService } from './base-api.service';

// User types
export interface User {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
    role: UserRole;
    status: UserStatus;
    createdAt: string;
    updatedAt: string;
}

export type UserRole = 'admin' | 'developer' | 'finance' | 'teacher' | 'user';
export type UserStatus = 'active' | 'suspended';

export interface UsersResponse {
    success: boolean;
    data: User[];
    pagination: {
        pageSize: number;
        nextCursor: string | null;
        hasNextPage: boolean;
    };
}

export interface UserResponse {
    success: boolean;
    data: User;
}

export interface CreateUserRequest {
    email: string;
    firstName?: string;
    lastName?: string;
    role?: UserRole;
}

export interface UpdateUserRequest {
    firstName?: string;
    lastName?: string;
    role?: UserRole;
    status?: UserStatus;
}

// API Service class for user management
class UsersApiService extends BaseApiService {
    // List all users with pagination
    async listUsers(params?: { cursor?: string; pageSize?: number; status?: UserStatus }): Promise<UsersResponse> {
        const queryParams = new URLSearchParams();
        if (params?.cursor) queryParams.append('cursor', params.cursor);
        if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
        if (params?.status) queryParams.append('status', params.status);

        const queryString = queryParams.toString();
        const url = `/api/auth/users${queryString ? `?${queryString}` : ''}`;
        const response = await this.client.get<UsersResponse>(url);
        return response.data;
    }

    // Get a single user by ID
    async getUser(id: number): Promise<UserResponse> {
        const response = await this.client.get<UserResponse>(`/api/auth/users/${id}`);
        return response.data;
    }

    // Create a new user
    async createUser(request: CreateUserRequest): Promise<UserResponse> {
        const response = await this.client.post<UserResponse>('/api/auth/users', request);
        return response.data;
    }

    // Update an existing user
    async updateUser(id: number, request: UpdateUserRequest): Promise<UserResponse> {
        const response = await this.client.put<UserResponse>(`/api/auth/users/${id}`, request);
        return response.data;
    }
}

// Export singleton instance
export const usersApi = new UsersApiService();
